import * as d3 from 'd3';

class FromToArcPlotView extends HTMLElement {
    // Save dimensions between renderings
    static dimensions = {
        width: 800,
        height: 600
    };

    connectedCallback() {
        // Listen for window resize to adjust size
        window.addEventListener('resize', this.handleResize.bind(this));
        
        // Listen for HTMX updates
        document.body.addEventListener('htmx:afterSwap', this.handleHtmxUpdate.bind(this));
        
        // Initial rendering
        this.renderFromToArcPlot();
    }
    
    disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize.bind(this));
        document.body.removeEventListener('htmx:afterSwap', this.handleHtmxUpdate.bind(this));
    }
    
    handleResize() {
        if (document.body.contains(this)) {
            const container = this.querySelector('#from-to-arc-container');
            if (container) {
                const rect = container.getBoundingClientRect();
                if (rect.width > 10 && rect.height > 10) {
                    // Update our static dimensions when the size changes
                    FromToArcPlotView.dimensions = {
                        width: rect.width,
                        height: rect.height
                    };
                }
            }
            this.renderFromToArcPlot();
        }
    }
    
    handleHtmxUpdate() {
        if (document.body.contains(this)) {
            this.renderFromToArcPlot();
        }
    }
    
    renderFromToArcPlot() {
        const dataEl = this.querySelector('#from-to-arc-data');
        if (!dataEl) return;
        
        try {
            const data = JSON.parse(dataEl.getAttribute('x-data'));
            this.createArcDiagram(data);
        } catch (error) {
            console.error('Failed to parse from-to-arc data:', error);
        }
    }

    createArcDiagram(data) {
        const container = this.querySelector('#from-to-arc-container');
        if (!container) return;
        
        // Use container dimensions if available, otherwise the saved ones
        const rect = container.getBoundingClientRect();
        const width = (rect.width > 10) ? rect.width : FromToArcPlotView.dimensions.width;
        const height = (rect.height > 10) ? rect.height : FromToArcPlotView.dimensions.height;
        
        // If it's a "real" size, save it for future use
        if (width > 10 && height > 10) {
            FromToArcPlotView.dimensions = { width, height };
        }
        
        console.log('FromToArcPlot dimensions:', width, height);
        const step = 22;
        const marginTop = 20;
        const marginBottom = 20;
        const marginLeft = 220;

        const toCount = {};
        const links = [];
        const nodeSet = new Set();

        Object.values(data.children).forEach(d => {
                nodeSet.add(d.from);
                nodeSet.add(d.to);
                toCount[d.to] = (toCount[d.to] || 0) + 1;
                links.push({ source: d.from, target: d.to });
        });
        const nodes = Array.from(nodeSet).map(id => ({
                id,
                group: toCount[id] ? id : null
        }));
        const sorted = [];
        const placed = new Set();
        links.forEach(link => {
                if (!placed.has(link.source)) {
                        sorted.push(nodes.find(n => n.id === link.source));
                        placed.add(link.source);
                }
                if (!placed.has(link.target)) {
                        sorted.push(nodes.find(n => n.id === link.target));
                        placed.add(link.target);
                }
        });
        nodes.forEach(n => {
                if (!placed.has(n.id)) sorted.push(n);
        });
        // Scale and color
                
        const y = d3.scalePoint(sorted.map(d => d.id), [marginTop, height - marginBottom]);
        const Y = new Map(sorted.map(d => [d.id, y(d.id)]));
        const color = d3.scaleOrdinal()
                .domain(nodes.map(d => d.group || d.id))
                .range(d3.schemeCategory10);

        //remove existing SVG
        d3.select(container).selectAll("svg").remove();
        // Set up new SVG
        const svg = d3.select(container)
                .append("svg")
                .attr("width", width)
                .attr("height", height)
                .attr("viewBox", [0, 0, width, height])
                .attr("style", "max-width: 100%; height: auto;");

      

        // Draw arcs
        function arc(d) {
                const y1 = Y.get(d.source);
                const y2 = Y.get(d.target);
                const r = Math.abs(y2 - y1) / 2;
                return `M${marginLeft},${y1}A${r},${r} 0,0,${y1 < y2 ? 1 : 0} ${marginLeft},${y2}`;
        }
        const path = svg.append("g")
                .attr("fill", "none")
                .attr("stroke-opacity", 0.6)
                .attr("stroke-width", 1.5)
                .selectAll("path")
                .data(links)
                .join("path")
                .attr("stroke", d => color(d.target))
                .attr("d", arc);

        // Draw nodes and labels
        const label = svg.append("g")
                .attr("font-family", "sans-serif")
                .attr("font-size", 11)
                .attr("text-anchor", "end")
                .selectAll("g")
                .data(sorted)
                .join("g")
                .attr("transform", d => `translate(${marginLeft},${Y.get(d.id)})`);

        label.append("text")
                .attr("x", -8)
                .attr("dy", "0.35em")
                .attr("fill", "#222")
                .text(d => d.id);

        // Only to-nodes get a circle
        label.append("circle")
                .attr("r", d => d.group ? 4 : 0) // Only to-nodes get a circle
                .attr("fill", d => color(d.group || d.id));

        // Hover effect
        label.append("rect")
                .attr("x", -marginLeft)
                .attr("y", -step / 2)
                .attr("width", marginLeft + 40)
                .attr("height", step)
                .attr("fill", "transparent")
                .on("mouseenter", function(event, d) {
                        // Find all connections where this node is from or to
                        const connected = new Set();
                        links.forEach(l => {
                                if (l.source === d.id || l.target === d.id) {
                                        connected.add(l.source);
                                        connected.add(l.target);
                                }
                        });

                        // Highlight arcs
                        path.attr("stroke-opacity", l => (l.source === d.id || l.target === d.id ? 1 : 0.1));

                        // Highlight both from and to text in black and bold
                        label.selectAll("text")
                                .attr("font-weight", n => connected.has(n.id) ? "bold" : "normal")
                                .attr("fill", n => connected.has(n.id) ? "#222" : "#aaa");

                        // Only to-nodes get a circle, and it lights up if connected
                        label.selectAll("circle")
                                .attr("fill", n => (n.group && connected.has(n.id)) ? color(n.group) : "#eee");
                })
                .on("mouseleave", function() {
                        path.attr("stroke-opacity", 0.6);
                        label.selectAll("text")
                                .attr("font-weight", "normal")
                                .attr("fill", "#222");
                        label.selectAll("circle")
                                .attr("fill", d => d.group ? color(d.group) : "transparent");
                });
        }

};

if (!customElements.get('from-to-arc-view')) {
        customElements.define('from-to-arc-view', FromToArcPlotView);
}
